import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { RootReducer } from "src/redux/reducers";
import { getUserInfoActions } from "src/redux/actions/user";
import { resetStateAction } from "src/redux/actions/auth";
import { signOutUser } from "src/services/localStorage";
import flagSmithIdentification from "src/helpers/flagsmithIdentification";
import useUserType from "src/hooks/useUserType";
import useSchools from "src/hooks/useSchools";
import NotificationCenter from "src/components/NotificationCenter/NotificationCenter";
import ClassSwitcher from "src/components/ClassSwitcher/ClassSwitcher";
import ActiveSchoolSelect from "src/components/ActiveSchoolSelect/ActiveSchoolSelect";
import userImage from "src/assets/images/user1.png";
import { StyledManenoLayout } from "./ManenoLayout.styles";

import {
  IconDashboard,
  IconProgress,
  IconLibrary,
  IconTeacherResources,
  IconAssignments,
  IconReports,
  IconSchools,
  IconSignOut
} from "../ManenoIcons";

interface ManenoLayoutProps {
  children: React.ReactNode;
}

const ManenoLayout = ({ children }: ManenoLayoutProps) => {
  const [t] = useTranslation();
  const { userData, lastUpdated } = useSelector((state: RootReducer) => state.user);
  const { authData } = useSelector((state: RootReducer) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserType();
  const { activeSchoolId } = useSchools();

  const [progressOpen, setProgressOpen] = useState(false);

  const isProgressActive = location.pathname.startsWith("/progress-report");
  const isAdminUserNoSchools = userData && isAdmin && !activeSchoolId;

  const flagSmithHelper = flagSmithIdentification();

  const isDataStale = () => {
    const oneDay = 24 * 60 * 60 * 1000;
    return !lastUpdated || Date.now() - lastUpdated > oneDay;
  };

  const getUserInfo = useCallback(
    () => dispatch(getUserInfoActions.request({ userId: authData?.userId || "" })),
    [dispatch, authData?.userId]
  );

  useEffect(() => {
    if (isProgressActive) setProgressOpen(true);
  }, [isProgressActive]);

  useEffect(() => {
    if (!userData || isDataStale()) {
      getUserInfo();
    } else {
      flagSmithHelper.identify(userData.id, userData.schoolId);
    }
  }, []);

  const handleLogout = () => {
    dispatch(resetStateAction);
    signOutUser();
    navigate("/login");
  };

  const firstName = userData?.firstName ?? userData?.name ?? "";

  return (
    <StyledManenoLayout>
      {/* ── Header ──────────────────────────────────────────── */}
      <header className="maneno-header">
        <button className="header-brand" onClick={() => navigate("/dashboard")} aria-label="Go to dashboard">
          <span className="maneno-brand-icon">M</span>
          <span className="maneno-logo-text">Maneno</span>
        </button>

        <div className="header-right">
          {isAdmin ? <ActiveSchoolSelect /> : <ClassSwitcher />}
          <NotificationCenter />

          <div className="header-user-pill" onClick={() => navigate("/dashboard")}>
            <div className="header-avatar">
              {userData?.profileImageUrl ? (
                <img src={userData.profileImageUrl} alt="" aria-hidden="true" />
              ) : (
                <img src={userImage} alt="" aria-hidden="true" />
              )}
            </div>
            <span className="header-username">{firstName || "Admin"}</span>
            <span className="header-chevron">&#9662;</span>
          </div>

          <button className="header-logout-btn" onClick={handleLogout}>
            {t("sidebar_button_logout_label")}
          </button>
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────── */}
      <div className="maneno-body">
        <aside className="maneno-sidebar" aria-label="Sidebar">
          <span className="sidebar-section-label">{t("sidebar_menu_label", "Menu")}</span>

          <nav className="sidebar-nav" aria-label="Main navigation">
            {!isAdminUserNoSchools && (
              <NavLink to="/dashboard" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
                <IconDashboard />
                <span className="nav-label">{t("sidebar_link_dashboard")}</span>
              </NavLink>
            )}

            {!isAdminUserNoSchools && (
              <div className="sidebar-nav-group">
                <button
                  className={`sidebar-nav-group-trigger${progressOpen ? " open" : ""}${
                    isProgressActive ? " open" : ""
                  }`}
                  onClick={() => setProgressOpen((v) => !v)}
                  aria-expanded={progressOpen || isProgressActive}
                >
                  <IconProgress />
                  <span className="nav-label">{t("sidebar_link_progress_report")}</span>
                  <span className="nav-chevron">&#9662;</span>
                </button>
                <div className={`sidebar-submenu${progressOpen || isProgressActive ? " open" : ""}`}>
                  <NavLink
                    to="/progress-report-detailed"
                    className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                  >
                    {t("sidebar_link_progress_detailed")}
                  </NavLink>
                  <NavLink
                    to="/progress-report-class"
                    className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                  >
                    {t("sidebar_link_progress_class")}
                  </NavLink>
                  <NavLink
                    to="/progress-report-students"
                    className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                  >
                    {t("sidebar_link_progress_students")}
                  </NavLink>
                </div>
              </div>
            )}

            <NavLink to="/library" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
              <IconLibrary />
              <span className="nav-label">{t("sidebar_link_book_library")}</span>
            </NavLink>

            <NavLink
              to="/teacher-resources"
              className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}
            >
              <IconTeacherResources />
              <span className="nav-label">{t("sidebar_link_teacher_resources")}</span>
            </NavLink>

            {isAdmin && (
              <NavLink to="/reports" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
                <IconReports />
                <span className="nav-label">Reports</span>
              </NavLink>
            )}

            {!isAdminUserNoSchools && (
              <NavLink to="/assignments" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
                <IconAssignments />
                <span className="nav-label">{t("sidebar_link_assignments", "Assignments")}</span>
              </NavLink>
            )}

            {isAdmin && (
              <NavLink to="/schools" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
                <IconSchools />
                <span className="nav-label">{t("sidebar_link_schools", "Schools")}</span>
              </NavLink>
            )}
          </nav>

          <div className="sidebar-divider" />

          <div className="sidebar-footer">
            <button
              className="sidebar-nav-item"
              onClick={handleLogout}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer" }}
            >
              <IconSignOut />
              <span className="nav-label">{t("sidebar_button_logout_label")}</span>
            </button>
          </div>
        </aside>

        <main className="maneno-content">{children}</main>
      </div>
    </StyledManenoLayout>
  );
};

export default ManenoLayout;
